import { Button, Grid, TextField } from '@mui/material'
import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { enabledFeatures } from 'config/env'
import { getLanguage } from 'data/locale'
import { useTitle } from 'hooks'
import { checkIpRange, clearAuthState, completeCredentialLogin } from 'store/actions/user'
import backend, { isAuthenticated, isLibraryMode } from 'utils/backend'
import { useHotjar } from 'utils/hotjar'

import LoginErrorModal from 'components/LoginErrorModal'
import LoginSidePanel from 'components/LoginSidePanel'
import ResetPassword from 'components/PasswordReset'

import imagineLogoMobile from '../../assets/imagine-logo-mobile.svg'

const Login = () => {
  const dispatch = useDispatch()
  const { t } = getLanguage()

  const noRemoteSeats = useSelector(state => state.user.noRemoteSeats)

  const initialState = {
    user: '',
    userError: null,
    password: '',
    passwordError: null,
    challengeId: null,
    challengeSecret: null,
    code: '',
    codeError: null,
    codeLocked: false,
    modalOpen: false
  }
  const [state, setState] = useState(initialState)

  useTitle(() => null)

  useHotjar()

  /* Backend module does not have access to the store, so when token refresh fails,
   * it clears local storage and redirects to /login, but internally the app state
   * still believes the user is authenticated. This hook ensures the state gets
   * properly updated. If the user was previously authenticated via IP range, we
   * attempt to re-authenticate.
   */
  useEffect(() => {
    if (!isAuthenticated()) {
      if (isLibraryMode()) {
        /* The only way for both isLibraryMode to be true and isAuthenticated to be
         * false is that the previous session was IP-authenticated and the refresh
         * token has expired, so we try to re-authenticate via IP range.
         */
        dispatch(checkIpRange())
      } else {
        dispatch(clearAuthState())
      }
    }
  }, [])

  const handleInputChange = ({ target }) => {
    // set the state and clean error based on target name and value
    setState(state => ({
      ...state,
      [target.name]: target.value,
      [`${target.name}Error`]: null
    }))
  }

  const handleSubmit = e => {
    e.preventDefault()
    if (!state.user || !state.password) {
      setState(state => ({
        ...state,
        ...(!state.user && { userError: t.user.fieldRequired }),
        ...(!state.password && { passwordError: t.user.fieldRequired })
      }))
    } else {
      const submitCredentials = enabledFeatures.includes('LOGIN_MFA')
        ? async () => {
            try {
              const {
                data: { challengeId, secret: challengeSecret }
              } = await backend.post('api/login/', {
                username: state.user,
                password: state.password
              })
              setState(state => ({ ...state, challengeId, challengeSecret }))
            } catch (err) {
              const msg = err.response?.data?.detail
              setState(state => ({
                ...state,
                userError: msg || 'Invalid credentials'
              }))
            }
          }
        : async () => {
            try {
              const { data } = await backend.post('api/token/', {
                username: state.user,
                password: state.password
              })
              dispatch(
                completeCredentialLogin({ accessToken: data.access, refreshToken: data.refresh })
              )
            } catch (err) {
              const msg = err.response?.data?.detail
              setState(state => ({
                ...state,
                userError: msg || 'Invalid credentials'
              }))
            }
          }
      submitCredentials()
    }
  }

  const handleCodeSubmit = e => {
    e.preventDefault()
    if (!state.code) {
      setState(state => ({
        ...state,
        codeError: t.user.fieldRequired
      }))
    } else if (!/^\d{6}$/.test(state.code)) {
      setState(state => ({
        ...state,
        codeError: t.user.verifyLogin.codeMustBe6Digits
      }))
    } else {
      const submitCode = async () => {
        try {
          const { data } = await backend.post('api/login/verify/', {
            challenge_id: state.challengeId,
            secret: state.challengeSecret,
            password: state.code
          })
          dispatch(
            completeCredentialLogin({ accessToken: data.access, refreshToken: data.refresh })
          )
        } catch (err) {
          if (!err.response) {
            setState(state => ({
              ...state,
              userError: 'Network error'
            }))
            return
          }
          const code = err.response.data?.code ?? 'unknown'
          const msg = err.response.data?.detail
          setState(state => ({
            ...state,
            codeError: msg || 'Invalid verification code',
            codeLocked: code !== 'invalid'
          }))
        }
      }
      submitCode()
    }
  }

  useEffect(() => {
    if (noRemoteSeats) {
      setState(state => ({ ...state, modalOpen: true }))
    }
  }, [noRemoteSeats])

  return (
    <div>
      <Grid container>
        <LoginSidePanel />
        <Grid size={{ xs: 12, md: 9 }}>
          <div className="login__right">
            {/* Keep commented for future use */}
            {/* <h4 className="maintenanceMessage">{t.global.maintenanceMessage}</h4> */}
            {state.challengeId ? (
              <>
                <h3>{t.user.verifyLogin.title}</h3>
                <p>{t.user.verifyLogin.text}</p>
                <form className="Login" onSubmit={handleCodeSubmit}>
                  <TextField
                    placeholder={t.user.verifyLogin.placeholder}
                    variant="outlined"
                    name="code"
                    slotProps={{
                      htmlInput: {
                        autoComplete: 'off',
                        maxLength: 6,
                        style: { textAlign: 'center' }
                      }
                    }}
                    onChange={handleInputChange}
                    error={!!state.codeError}
                    helperText={state.codeError}
                    disabled={state.codeLocked}
                  />
                  <Button type="submit" variant="contained" disabled={state.codeLocked}>
                    {t.user.verifyLogin.verify}
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    onClick={() => {
                      setState({ ...initialState })
                    }}
                  >
                    {t.user.verifyLogin.backToLogin}
                  </Button>
                </form>
              </>
            ) : (
              <>
                <h3>{t.global.loginFormText}</h3>
                <form className="Login" onSubmit={handleSubmit}>
                  <TextField
                    placeholder={t.user.user}
                    label={t.user.user}
                    variant="standard"
                    name="user"
                    onChange={handleInputChange}
                    error={!!state.userError}
                    helperText={state.userError}
                  />
                  <TextField
                    placeholder={t.user.password}
                    label={t.user.password}
                    variant="standard"
                    type="password"
                    name="password"
                    onChange={handleInputChange}
                    error={!!state.passwordError}
                    helperText={state.passwordError}
                  />
                  <Button type="submit" variant="contained">
                    {t.user.login}
                  </Button>
                </form>
                <ResetPassword />
                <a href={t.user.noAccountUrl}>
                  {t.user.noAccount} - {t.user.register}
                </a>
              </>
            )}
          </div>
        </Grid>
        <img src={imagineLogoMobile} alt="Imagine Canada" className="login__imagine-logo-mobile" />
      </Grid>
      <LoginErrorModal
        isOpen={state.modalOpen}
        dismiss={() => {
          setState({ ...state, modalOpen: false })
        }}
      />
    </div>
  )
}

export default Login
